import { useEffect, useState } from "react";
import { Plus, Trash2, Search, Sparkles, X, FileText, Video, Download, Wand2 } from "lucide-react";
import api, { apiError, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [aiEdit, setAiEdit] = useState(null);
  const [editing, setEditing] = useState(false);

  const runAiEdit = async () => {
    setEditing(true);
    try {
      const { data } = await api.post(`/media/${lightbox.id}/ai-edit`, { instruction: aiEdit.trim() });
      toast.success(`Variante IA créée : ${data.name} — elle est dans la médiathèque`);
      setAiEdit(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEditing(false); }
  };

  const load = () => api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/media/search?q=${encodeURIComponent(query.trim())}${clientId ? `&client_id=${clientId}` : ""}`)
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query, clientId]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      try { await uploadFile(file, clientId ? { client_id: clientId } : {}); } catch { toast.error(`Échec : ${file.name}`); }
    }
    toast.success(`${files.length} fichier(s) ajouté(s) — analyse IA en arrière-plan`);
    setTimeout(load, 1500);
    e.target.value = "";
  };

  const analyze = async (m) => {
    setAnalyzing(m.id);
    try {
      await api.post(`/media/${m.id}/analyze`);
      toast.success("Média analysé par l'IA");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAnalyzing(null); }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const filtered = clientId ? media.filter((m) => m.client_id === clientId) : media;
  const isImage = (m) => /\.(png|jpe?g|gif|webp)$/i.test(m.name || "");
  const isVideoFile = (m) => /\.(mp4|mov|webm)$/i.test(m.name || "");
  const isVisual = (m) => isImage(m) || isVideoFile(m);
  const shown = (results ?? filtered).filter(isVisual);

  return (
    <div className="space-y-5" data-testid="media-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-heading text-3xl font-bold">Bibliothèque média</h1>
          <p className="text-neutral-500 text-sm mt-1">Photos, vidéos, logos et templates de tous vos clients — analysés par IA.</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="media-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Recherche IA : « bague en or », « selfie boutique »…"
            className="text-sm border border-neutral-200 rounded-md pl-9 pr-8 py-2 bg-white w-80 max-w-full"
          />
          {query && (
            <button data-testid="media-search-clear" onClick={() => setQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <select data-testid="media-client-filter" value={clientId} onChange={(e) => setClientId(e.target.value)}
          className="text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white">
          <option value="">Tous les clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label>
          <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={upload} data-testid="media-global-upload" />
          <span className="inline-flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white rounded-md px-4 py-2 text-sm cursor-pointer transition-colors">
            <Plus className="w-4 h-4" /> Téléverser
          </span>
        </label>
      </div>

      {results !== null && (
        <p className="text-sm text-neutral-500" data-testid="media-search-count">
          {searching ? "Recherche en cours…" : `${results.length} résultat(s) pour « ${query.trim()} »`}
        </p>
      )}

      {shown.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-12 text-center" data-testid="media-empty-global">
          <p className="text-sm text-neutral-400">
            {results !== null ? "Aucun média ne correspond à cette recherche." : "Aucun média pour le moment. Téléversez vos premiers fichiers."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {shown.map((m) => (
            <div key={m.id} className="relative group rounded-xl overflow-hidden border border-neutral-200 bg-white aspect-square" data-testid={`media-item-${m.id}`}>
              <button className="w-full h-full block" onClick={() => { setLightbox(m); setAiEdit(null); }} data-testid={`media-open-${m.id}`} title="Agrandir">
                {isImage(m) ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${m.preview_url || m.url}`} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-neutral-100 text-neutral-400 p-2">
                    {isVideoFile(m) ? <Video className="w-8 h-8" /> : <FileText className="w-8 h-8" />}
                    <span className="text-[10px] mt-1.5 truncate max-w-full">{m.name}</span>
                  </div>
                )}
              </button>
              {m.analyzed_at && (
                <span className="absolute top-1.5 left-1.5 bg-neutral-900/70 text-white rounded-full p-1" title="Analysé par IA">
                  <Sparkles className="w-3 h-3" />
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center justify-between gap-1">
                  <span className="truncate">{m.name} · {clientName(m.client_id)}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {isImage(m) && !m.analyzed_at && (
                      <button onClick={() => analyze(m)} data-testid={`media-analyze-${m.id}`} title="Analyser avec l'IA"
                        disabled={analyzing === m.id}>
                        <Sparkles className={`w-3 h-3 ${analyzing === m.id ? "animate-pulse" : ""}`} />
                      </button>
                    )}
                    <button onClick={async () => { await api.delete(`/media/${m.id}`); load(); }} data-testid={`media-delete-${m.id}`}>
                      <Trash2 className="w-3 h-3 shrink-0" />
                    </button>
                  </span>
                </div>
                {m.ai_description && <p className="truncate mt-0.5 text-neutral-300">{m.ai_description}</p>}
                {m.tags?.length > 0 && (
                  <p className="truncate mt-0.5 text-neutral-400" data-testid={`media-tags-${m.id}`}>
                    {m.tags.slice(0, 4).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!lightbox} onOpenChange={(v) => { if (!v) { setLightbox(null); setAiEdit(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="media-lightbox" aria-describedby={undefined}>
          {lightbox && (
            <>
              <DialogHeader><DialogTitle className="font-heading pr-6 truncate">{lightbox.name}</DialogTitle></DialogHeader>
              <div className="max-h-[55vh] flex items-center justify-center bg-neutral-100 rounded-lg overflow-hidden">
                {isVideoFile(lightbox) ? (
                  <video src={`${process.env.REACT_APP_BACKEND_URL}${lightbox.url}`} controls className="max-h-[55vh] w-full object-contain" data-testid="lightbox-video" />
                ) : (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${lightbox.url}`} alt={lightbox.name} className="max-h-[55vh] w-full object-contain" data-testid="lightbox-img" />
                )}
              </div>
              {lightbox.ai_description && <p className="text-sm text-neutral-600">{lightbox.ai_description}</p>}
              {lightbox.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {lightbox.tags.map((t, i) => <span key={i} className="text-[10px] bg-neutral-100 border border-neutral-200 rounded-full px-2 py-0.5">{t}</span>)}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}${lightbox.url}`} download={lightbox.name} data-testid="lightbox-download"
                  className="inline-flex items-center gap-1.5 border border-neutral-200 rounded-md px-3 py-2 text-sm hover:bg-neutral-50 transition-colors">
                  <Download className="w-4 h-4" /> Télécharger
                </a>
                {isImage(lightbox) && (
                  <Button variant="outline" data-testid="lightbox-ai-edit" onClick={() => setAiEdit(aiEdit === null ? "" : null)} className="gap-1.5">
                    <Wand2 className="w-4 h-4" /> Modifier par IA
                  </Button>
                )}
                <button data-testid="lightbox-delete"
                  onClick={async () => { await api.delete(`/media/${lightbox.id}`); setLightbox(null); load(); toast.success("Média supprimé"); }}
                  className="ml-auto inline-flex items-center gap-1.5 border border-neutral-200 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-4 h-4" /> Supprimer
                </button>
              </div>
              {aiEdit !== null && (
                <div className="flex gap-2">
                  <input data-testid="lightbox-ai-input" value={aiEdit} onChange={(e) => setAiEdit(e.target.value)}
                    placeholder="Ex : fond doré, noir et blanc, ambiance studio…"
                    className="flex-1 text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white" />
                  <Button data-testid="lightbox-ai-submit" disabled={editing || !aiEdit.trim()} onClick={runAiEdit}
                    className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
                    {editing ? "Retouche IA en cours…" : "Appliquer"}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}